import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntake } from "../lib/intake-context-value";
import { API_BASE } from "../lib/api";
import { fireEvent } from "../lib/tracking";


export default function ConsentPage() {
  const navigate = useNavigate();
  const { token, prescreenResult, setConsentVersion } = useIntake();
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!agreed) {
      setError("You must agree to continue.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/intake/vesper5/consent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!json.success) {
        setError(json.error || "Consent recording failed.");
        return;
      }
      const version = json.data?.version;
      if (!version) {
        setError("Consent recording failed.");
        return;
      }
      setConsentVersion(version);
      fireEvent("consent", "vesper5-us", { consent_given: true, consent_version: version }, token);
      if (prescreenResult?.status === "qualified") {
        navigate("/qualified");
      } else {
        navigate("/not-qualified-registry");
      }
    } catch (e) { console.error("[intake] request failed", e);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-[24px] font-extralight text-gray-900" style={{ letterSpacing: "-0.4px" }}>
          Registry Consent
        </h1>
        <p className="text-[14px] text-gray-600">
          Please read and agree to the following before continuing.
        </p>
      </div>

      {error && (
        <div className="bg-[var(--color-status-danger-bg)] rounded-xl p-3 text-[13px] text-[var(--color-status-danger-text)]" role="alert">
          {error}
        </div>
      )}

      <div className="bg-white rounded-2xl p-6 space-y-4 max-h-[400px] overflow-y-auto" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <h2 className="text-[14px] font-semibold text-gray-900">
          Patient Registry Consent
        </h2>
        <div className="space-y-3 text-[13px] text-gray-600 leading-relaxed">
          <p>
            You are being asked to join the Leap21 Patient Registry. The registry collects health
            information from people interested in clinical research studies.
          </p>
          <p>
            Your information will be stored securely and used only to match you with future research
            studies you may qualify for.
          </p>
          <p>
            You may withdraw from the registry at any time by contacting us. Your decision to
            participate or not will not affect your medical care.
          </p>
          <p>
            This registry consent is separate from any future trial informed consent form (ICF).
            If you qualify for a specific study, you will be asked to sign a separate ICF.
          </p>
        </div>
      </div>

      <label className="flex items-start gap-3 bg-white rounded-xl p-4" style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="w-5 h-5 mt-0.5 rounded border-gray-300"
          aria-required="true"
        />
        <span className="text-[14px] text-gray-700">
          I have read and agree to the registry consent above. I understand this is not the same as
          a trial informed consent form.
        </span>
      </label>

      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full py-3 rounded-xl bg-gray-900 text-white text-[14px] font-medium hover:bg-gray-800 active:scale-[0.97] transition-all disabled:opacity-50"
      >
        {loading ? "Saving..." : "Agree and Continue"}
      </button>
    </div>
  );
}
